import { Outlet, createFileRoute, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, type JSX } from "react";

import { useAuth } from "@/shared/auth";
import { AppShell } from "@/shared/components/AppShell";

function AuthenticatedLayout(): JSX.Element {
  const { isAuthenticated, status, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (status !== "pending" && !isAuthenticated) {
      void navigate({ to: "/login" });
    }
  }, [isAuthenticated, status, navigate]);

  // Password-rotation gate (ADR-0020 / must_change_password), mirrors the
  // backend's Authenticate-middleware gate (internal/auth/http/middleware.go)
  // on the FE side: keep the user on /change-password until they rotate.
  useEffect(() => {
    if (isAuthenticated && user?.must_change_password && location.pathname !== "/change-password") {
      void navigate({ to: "/change-password", replace: true });
    }
  }, [isAuthenticated, user, location.pathname, navigate]);

  if (status === "pending") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <span className="text-sm text-[var(--text-subtle)]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <span className="text-sm text-[var(--text-subtle)]" />
      </div>
    );
  }

  // Forced password rotation is a locked-down, single-screen state: keep
  // AppShell mounted (so TooltipProvider never tears down mid-transition —
  // see AppShell's `minimal` doc comment) but skip its sidebar/topbar chrome
  // so it isn't visible or reachable, and so ChangePasswordPage's own
  // full-screen <main> doesn't nest inside AppShell's <main>.
  const lockedToChangePassword = Boolean(
    user?.must_change_password && location.pathname === "/change-password",
  );

  return (
    <AppShell minimal={lockedToChangePassword}>
      <Outlet />
    </AppShell>
  );
}

export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
});
